/**
 * W4e — what a server-owned ticket schedule state means, in words (tickets.schedule_state + schedule_state_reason,
 * 20270206060000 / 20270206063000 / 20270206064100). Pure; no I/O.
 *
 * A window that is not `placed` is never an authorized booking (D-5). The reserved-window overlap (W4d/W4e) is the
 * one this surface was added for: an approved rollover's reserved time grew over the ticket — it is marked
 * `conflict` immediately and, if no lane walk re-plans it within the recovery window, moved to `unplaced`
 * (Needs Placement). The grace period is a recovery window only, never an authorization of the overlap.
 */

export type ScheduleState = 'placed' | 'conflict' | 'unplaced' | 'carried';

export const RESERVED_WINDOW_OVERLAP = 'reserved_window_overlap' as const;

/** The reasons tickets_schedule_state_known admits (20270206063000). */
export const SCHEDULE_STATE_REASON_LABELS: Readonly<Record<string, string>> = {
  eod_overrun_unapproved: 'Runs past end of day (not approved)',
  eod_overrun_within_tolerance: 'Runs slightly past end of day (within tolerance)',
  eod_no_capacity_unapproved: 'No time left today (not approved)',
  protected_past_limit: 'Protected work past the limit',
  started_past_limit: 'Started work past the limit',
  rollover_approved_pending_build: 'Rollover approved — being planned',
  rollover_decision_required: 'Approved rollover needs a new decision',
  day_ended_unapproved: 'Day ended without a decision',
  rollover_carried: 'Carried to the next day',
  hours_unknown: 'Technician hours unknown',
  no_workday_in_horizon: 'No workday available',
  [RESERVED_WINDOW_OVERLAP]: 'Overlaps reserved rollover time',
  awaiting_external_resolution: 'Parked — waiting on an outside dependency (time released)',
  interrupted_ready_for_placement: 'Ready to resume — needs a new placement',
};

export interface ScheduleStatePresentation {
  /** Short label for a list row. */
  label: string;
  /** Why it matters, one sentence. */
  detail: string;
  /** Whether the stored window may be read as a valid booking. Only `placed` is. */
  bookable: boolean;
  tone: 'ok' | 'warning' | 'danger' | 'muted';
}

export function describeScheduleState(state: string | null | undefined, reason: string | null | undefined): ScheduleStatePresentation {
  const s = (state ?? 'placed') as ScheduleState | string;
  const reasonLabel = reason ? SCHEDULE_STATE_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ') : null;
  if (s === 'placed') return { label: 'Scheduled', detail: 'A valid window inside authorized hours.', bookable: true, tone: 'ok' };
  if (s === 'conflict' && reason === RESERVED_WINDOW_OVERLAP) {
    return {
      label: 'Schedule conflict — overlaps reserved rollover time',
      detail: 'Not a valid booking: the lane must be re-planned. If it is not re-planned shortly it moves to Needs Placement.',
      bookable: false, tone: 'danger',
    };
  }
  if (s === 'unplaced' && reason === RESERVED_WINDOW_OVERLAP) {
    return {
      label: 'Needs placement — reserved rollover time overlap',
      detail: 'Not completed or carried; it has no valid window and is waiting to be placed.',
      bookable: false, tone: 'danger',
    };
  }
  if (s === 'conflict') return { label: `Schedule conflict — ${reasonLabel ?? 'needs a person'}`, detail: 'Not an authorized booking.', bookable: false, tone: 'warning' };
  if (s === 'unplaced') return { label: `Needs placement — ${reasonLabel ?? 'no valid window'}`, detail: 'No valid window.', bookable: false, tone: 'danger' };
  if (s === 'carried') return { label: 'Carried', detail: 'History only; the continuation holds the work.', bookable: false, tone: 'muted' };
  return { label: reasonLabel ?? String(s), detail: 'Unknown schedule state — treated as not bookable.', bookable: false, tone: 'warning' };
}
